import React, { useContext, useEffect, useState } from "react";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { animated, useSpring } from "react-spring";
import { useParams } from "react-router-dom";
import CustomButton from "../../../components/Button/CustomButton";
import Comment from "./Comment";
import TestRequestApi from "../../../api/services/TestRequestApi";
import { UserContext } from "../../../contexts/UserContext";
import { ErrorSnackbarContext } from "../../../contexts/ErrorSnackbarContext";
import CustomButtonText from "../../../components/Button/CustomButtonText";
import { tesaBlue } from "../../../styles/colors";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import { TestRequestComment } from "../../../api/models/TestRequest";
import { fadeIn } from "../../../styles/springs";
import InfoBox from "../../../components/InfoBox";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(3),
    },
  }),
);

interface CommentsSectionProps {
  handleDirtyChange: (resetFlag?: boolean) => void;
  id: string;
}

const CommentsSection = (props: CommentsSectionProps) => {
  const { handleDirtyChange, id } = props;
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const classes = useStyles();
  const { testRequestId } = useParams();
  const { user } = useContext(UserContext);
  const { handleHttpError, showSnackbar, showErrorSnackbar } = useContext(ErrorSnackbarContext);
  const { description: descriptionPermissions } = useContext(TestRequestPermissionContext);

  const [hideInput, setHideInput] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [comments, setComments] = useState<TestRequestComment[] | null>(null);
  const springProps = useSpring(fadeIn);

  const fetchData = () => {
    if (testRequestId !== "new") {
      testRequestApi
        .getTestPagedRequestComments(testRequestId)
        .then((res) => {
          setComments(res || []);
        })
        .catch(handleHttpError);
    }
  };

  const handleBlur = () => {
    if (inputValue === "") {
      setHideInput(true);
    }
  };

  const handleCancel = () => {
    setInputValue("");
    setHideInput(true);
  };

  const catchBlockPromise = () => {
    handleHttpError();
    return new Promise<void>((reject) => {
      reject();
    });
  };

  const handleCreateComment = (): Promise<void> => {
    return testRequestApi
      .createTestRequestComment(testRequestId, inputValue)
      .then(() => {
        setInputValue("");
        setHideInput(true);
        fetchData();
        showSnackbar("Comment successfully saved", "success");
        return new Promise<void>((resolve) => {
          resolve();
        });
      })
      .catch(() => {
        catchBlockPromise();
      });
  };

  const handleDelete = (commentId: number) => {
    testRequestApi
      .deleteTestRequestComment(testRequestId, commentId)
      .then(() => {
        fetchData();
        showSnackbar("Comment successfully deleted", "success");
      })
      .catch(handleHttpError);
  };

  const handleSave = (commentId: number, payload: string): Promise<void> => {
    return testRequestApi
      .updateTestRequestComment(testRequestId, commentId, payload)
      .then(() => {
        setInputValue("");
        fetchData();
        return new Promise<void>((resolve) => {
          resolve();
        });
      })
      .catch(() => {
        catchBlockPromise();
      });
  };

  const localizeTime = (dateTime: string) => {
    const localDateTime = new Date(dateTime);
    const hours = localDateTime.getHours();
    const minutes = localDateTime.getMinutes();
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${hours}:${formattedMinutes}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <animated.div style={springProps} id={id}>
      <InfoBox
        compress
        text="Use the comment section to give information to the CSC lab (as a requester) or to the requester (as a CSC lab member) – it can also be used by stakeholders to give further information or convey similar customer requests."
      >
        <h3>Comments</h3>
      </InfoBox>
      {comments &&
        comments.map((comment, index) => {
          if (showAll) {
            return (
              <Comment
                date={new Date(comment.creationDateTime || "")}
                defaultValue={comment.comment}
                editPermission={comment.editable}
                handleCancel={handleCancel}
                handleDelete={handleDelete}
                handleDirtyChange={handleDirtyChange}
                handleSave={handleSave}
                id={comment.id}
                includeUpdateNotification
                key={comment.id}
                time={localizeTime(comment.creationDateTime)}
                username={comment.creator?.userName || ""}
              />
            );
          }
          if ((comments.length || 10) - 10 <= index && index <= (comments.length || 10)) {
            return (
              <Comment
                date={new Date(comment.creationDateTime || "")}
                defaultValue={comment.comment}
                editPermission={comment.editable}
                handleCancel={handleCancel}
                handleDelete={handleDelete}
                handleDirtyChange={handleDirtyChange}
                handleSave={handleSave}
                id={comment.id}
                includeUpdateNotification
                key={comment.id}
                time={localizeTime(comment.creationDateTime)}
                username={comment.creator?.userName || ""}
              />
            );
          }
          return <></>;
        })}
      <Comment
        date={new Date()}
        defaultFocused
        defaultValue=""
        editPermission
        hide={hideInput}
        handleBlur={handleBlur}
        handleCancel={handleCancel}
        handleChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
        handleCreate={handleCreateComment}
        handleDirtyChange={handleDirtyChange}
        time="just now"
        username={user?.userName || ""}
        value={inputValue}
      />
      {comments && comments.length > 10 && !showAll ? (
        <Box textAlign="center" margin="1rem 0 1rem 0">
          <CustomButtonText
            color={tesaBlue}
            label="Show All Comments"
            onMouseDown={() => {
              setShowAll(true);
              const descriptionCommentSection = document.getElementById("descriptionCommentSection");

              descriptionCommentSection?.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
              });
            }}
          />
        </Box>
      ) : (
        <></>
      )}
      <CustomButton
        className={classes.button}
        onClick={() => {
          if (descriptionPermissions?.addComment) {
            setInputValue("");
            setHideInput(false);
          } else {
            showErrorSnackbar("You do not have permission to write a comment");
          }
        }}
        add
        text="Write Comment"
      />
    </animated.div>
  );
};

export default CommentsSection;
